/**
 * @fileoverview Live Stadium Combat Stage and HUD Component for "Reacción Rápida (1 vs 1)" on stadium giant screens (/screen and /screen-v2).
 * Implements a Zero-Canvas architecture with GPU Layer Compositing (60 FPS, 0% CPU in idle),
 * instant 0ms sprite transitions without flicker, central tension countdown and trigger flash,
 * clean broadcast stadium winner proclamation (WITHOUT milliseconds), early fault red card alerts,
 * and hardware-accelerated ephemeral confetti strictly auto-destroyed after 5 seconds to guarantee Zero Memory Leaks.
 * 
 * AppSec & Robustness:
 * - String sanitization preventing control characters and UI overflow in contender/winner names.
 * - Deterministic timer lifecycle and unmount cleanup (confetti, trigger flash, preloads).
 * - Safe CSS url handling and immutable vector fallbacks on image loading failures.
 * - Strict Scheme Whitelisting for all dynamic sprite and background URLs.
 * 
 * @module components/Screen/Sections/QuickReaction/QuickReactionGame
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetConfigBL, GetThemeByIdBL } from '../../../../utilities/BL/QuickReaction';
import {
    ResolveQuickReactionMediaUrl,
    PreloadQuickReactionThemeAssets,
    GetQuickReactionSprite,
    DEFAULT_SCREEN_BG_SVG,
    DEFAULT_J1_FRONT_SVG,
    DEFAULT_J2_FRONT_SVG,
    EARLY_FAULT_CARD_SVG
} from './QuickReactionAssets';

/**
 * Paleta de colores para las partículas de confeti de victoria.
 * @constant {Array<string>}
 */
const CONFETTI_COLOR_PALETTE = ['#00f0ff', '#ff0055', '#ffea00', '#ffffff', '#a855f7', '#22c55e', '#f97316'];

/**
 * Total de partículas generadas para la explosión efímera de confeti.
 * @constant {number}
 */
const CONFETTI_PARTICLES_COUNT = 65;

/**
 * Sanitiza nombres de contendientes y textos de proclamación para AppSec.
 * 
 * @param {string|null|undefined} text - Texto a sanitizar.
 * @param {string} fallback - Valor por defecto.
 * @param {number} [maxLength=40] - Longitud máxima permitida.
 * @returns {string} Texto seguro y recortado.
 */
const sanitizePlayerName = (text, fallback = 'Jugador', maxLength = 40) => {
    if (typeof text !== 'string') return fallback;
    const clean = text.replace(/[\x00-\x1F\x7F]/g, '').trim();
    return clean.length > 0 ? clean.slice(0, maxLength) : fallback;
};

/**
 * Componente QuickReactionGame: Escenario de combate 1 vs 1 a 60 FPS para Pantalla Gigante.
 * 
 * @function QuickReactionGame
 * @param {Object} props - Propiedades del componente.
 * @param {Object} [props.payload] - Paquete WebSocket SignalR emitido desde el backend.
 * @param {Object} [props.currentMatchState] - Estado de partida inyectado directamente por Screen.js.
 * @param {Object} [props.theme] - Tema visual inyectado directamente.
 * @param {Object} [props.config] - Configuración de sala inyectada.
 * @param {string} [props.roomId] - Identificador GUID de la sala.
 * @returns {React.ReactElement} Escenario de combate y HUD de estadio renderizado.
 */
export const QuickReactionGame = ({
    payload,
    currentMatchState,
    theme: propTheme,
    config: propConfig,
    roomId
}) => {
    // =========================================================================
    // 1. ESTADO DEL TEMA Y CONFIGURACIÓN
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // =========================================================================
    // 2. ESTADO AUTORITATIVO DE LA PARTIDA (TELEMETRÍA Y FASES)
    // =========================================================================
    const [matchState, setMatchState] = useState({
        phase: 'game_ready', // 'idle' | 'cover' | 'game_ready' | 'countdown' | 'random_wait' | 'trigger_active' | 'finished'
        countdownSeconds: 0,
        player1: {
            name: 'Jugador 1',
            side: 'player1',
            isConnected: false,
            reactionTimeMs: null,
            earlyFault: false,
            spriteBackUrl: null,
            spriteFrontUrl: null,
            spriteShootUrl: null,
            spriteVictoryUrl: null
        },
        player2: {
            name: 'Jugador 2',
            side: 'player2',
            isConnected: false,
            reactionTimeMs: null,
            earlyFault: false,
            spriteBackUrl: null,
            spriteFrontUrl: null,
            spriteShootUrl: null,
            spriteVictoryUrl: null
        },
        winnerSide: null, // 'J1' | 'J2' | 'Tie' | 'None'
        winnerName: null,
        winReason: null, // 'FASTEST_TIME' | 'OPPONENT_FAULT' | 'TIMEOUT' | 'TIE' | 'ADMIN_FORCE'
        enableEarlyFault: true,
        displayMode: 'single_target'
    });

    // =========================================================================
    // 3. ESTADOS VISUALES DE ANIMACIÓN (ZERO-CANVAS GPU)
    // =========================================================================
    const [isTriggerFlashActive, setIsTriggerFlashActive] = useState(false);
    const [ephemeralConfettiList, setEphemeralConfettiList] = useState([]);
    const confettiTimerRef = useRef(null);
    const triggerFlashTimerRef = useRef(null);

    /**
     * Carga de forma segura la configuración y tema si no fueron suministrados vía props.
     * 
     * @async
     * @function fetchActiveThemeConfiguration
     */
    const fetchActiveThemeConfiguration = useCallback(async () => {
        if (!roomId) return;
        try {
            const configResponse = await GetConfigBL(roomId);
            if (!isMountedRef.current) return;

            if (configResponse && configResponse.success && configResponse.data) {
                const roomConfig = configResponse.data;
                if (roomConfig.activeTheme) {
                    setActiveThemeData(roomConfig.activeTheme);
                    PreloadQuickReactionThemeAssets(roomConfig.activeTheme);
                } else if (roomConfig.activeThemeId) {
                    const themeResponse = await GetThemeByIdBL(roomConfig.activeThemeId, roomId);
                    if (!isMountedRef.current) return;
                    if (themeResponse && themeResponse.success && themeResponse.data) {
                        setActiveThemeData(themeResponse.data);
                        PreloadQuickReactionThemeAssets(themeResponse.data);
                    }
                }
            }
        } catch (fetchError) {
            console.warn('[QuickReactionGame] Error al cargar configuración remota:', fetchError);
        }
    }, [roomId]);

    // Sincronización del tema visual
    useEffect(() => {
        if (propTheme) {
            const directTheme = propTheme.theme && typeof propTheme.theme === 'object' ? propTheme.theme : propTheme;
            setActiveThemeData(directTheme);
            PreloadQuickReactionThemeAssets(directTheme);
            return;
        }

        if (propConfig) {
            const confTheme = propConfig.theme || propConfig.activeTheme;
            if (confTheme) {
                setActiveThemeData(confTheme);
                PreloadQuickReactionThemeAssets(confTheme);
                return;
            }
        }

        if (payload) {
            const incomingTheme = payload.theme ||
                payload.state?.theme ||
                payload.data?.theme ||
                payload.payload?.theme;

            if (incomingTheme) {
                setActiveThemeData(incomingTheme);
                PreloadQuickReactionThemeAssets(incomingTheme);
                return;
            }
        }

        if (!activeThemeData) {
            fetchActiveThemeConfiguration();
        }
    }, [payload, propTheme, propConfig, activeThemeData, fetchActiveThemeConfiguration]);

    // =========================================================================
    // 4. SINCRONIZACIÓN DE MENSAJES SIGNALR Y ESTADO EN VIVO
    // =========================================================================
    useEffect(() => {
        // A. Prioridad 1: Estado directo inyectado por Screen.js (currentMatchState)
        if (currentMatchState && typeof currentMatchState === 'object') {
            const stateObj = currentMatchState.state || currentMatchState;
            applyIncomingState(stateObj);
            return;
        }

        // B. Prioridad 2: Mensaje WebSocket recibido (payload)
        if (payload && typeof payload === 'object') {
            const stateObj = payload.state || payload.payload?.state || payload.data?.state || payload.payload || payload.data || payload;
            applyIncomingState(stateObj);
        }
    }, [currentMatchState, payload]);

    /**
     * Aplica e integra el estado del juego proveniente del servidor autoritativo.
     * 
     * @function applyIncomingState
     * @param {Object} stateObj - Objeto de estado autoritativo.
     */
    const applyIncomingState = (stateObj) => {
        if (!stateObj || typeof stateObj !== 'object') return;

        setMatchState((prevState) => {
            const rawPhase = stateObj.phase || stateObj.currentState || prevState.phase;
            const phase = String(rawPhase || '').toLowerCase();

            const countdownSeconds = typeof stateObj.countdownSeconds === 'number'
                ? stateObj.countdownSeconds
                : (typeof stateObj.count === 'number' ? stateObj.count : prevState.countdownSeconds);

            const p1Incoming = stateObj.player1 || stateObj.Player1 || {};
            const p2Incoming = stateObj.player2 || stateObj.Player2 || {};

            const winnerSide = stateObj.winnerSide ?? stateObj.winner ?? (stateObj.faultSide ? (stateObj.winnerSide || (stateObj.faultSide === 'J1' ? 'J2' : 'J1')) : prevState.winnerSide);
            const rawWinnerName = stateObj.winnerName ?? prevState.winnerName;
            const winnerName = rawWinnerName ? sanitizePlayerName(rawWinnerName, rawWinnerName, 40) : null;
            const winReason = stateObj.winReason ?? prevState.winReason;
            const enableEarlyFault = stateObj.enableEarlyFault !== undefined ? stateObj.enableEarlyFault : prevState.enableEarlyFault;
            const displayMode = stateObj.displayMode || stateObj.screenDisplayMode || prevState.displayMode || 'single_target';

            // Extraer faltas tempranas explícitas
            const p1EarlyFault = Boolean(p1Incoming.earlyFault || stateObj.faultSide === 'J1' || stateObj.player1EarlyFault || prevState.player1.earlyFault);
            const p2EarlyFault = Boolean(p2Incoming.earlyFault || stateObj.faultSide === 'J2' || stateObj.player2EarlyFault || prevState.player2.earlyFault);

            // Sanitización estricta de nombres de jugadores
            const p1Name = sanitizePlayerName(p1Incoming.name || stateObj.player1Name, prevState.player1.name || 'Jugador 1', 40);
            const p2Name = sanitizePlayerName(p2Incoming.name || stateObj.player2Name, prevState.player2.name || 'Jugador 2', 40);

            // Si se detectó disparo oficial (trigger_active), detonar destello de pantalla
            if (phase === 'trigger_active' && prevState.phase !== 'trigger_active') {
                triggerScreenFlash();
            }

            // Si la partida finalizó con ganador, detonar confeti efímero de 5 segundos
            if (phase === 'finished' && prevState.phase !== 'finished' && winnerSide && winnerSide !== 'None') {
                spawnEphemeralConfetti();
            }

            // Si la partida cambia a cualquier fase que no sea finished, limpiar confeti y destellos
            if (phase !== 'finished' && prevState.phase === 'finished') {
                clearConfettiAndTimers();
            }

            if (phase === 'game_ready' || phase === 'countdown' || phase === 'idle' || phase === 'random_wait') {
                if (triggerFlashTimerRef.current && phase !== 'trigger_active') {
                    clearTimeout(triggerFlashTimerRef.current);
                    triggerFlashTimerRef.current = null;
                    setIsTriggerFlashActive(false);
                }
            }

            return {
                phase,
                countdownSeconds,
                player1: {
                    ...prevState.player1,
                    ...p1Incoming,
                    name: p1Name,
                    isConnected: p1Incoming.isConnected !== undefined ? p1Incoming.isConnected : (Boolean(p1Incoming.connectionId) || prevState.player1.isConnected),
                    earlyFault: p1EarlyFault
                },
                player2: {
                    ...prevState.player2,
                    ...p2Incoming,
                    name: p2Name,
                    isConnected: p2Incoming.isConnected !== undefined ? p2Incoming.isConnected : (Boolean(p2Incoming.connectionId) || prevState.player2.isConnected),
                    earlyFault: p2EarlyFault
                },
                winnerSide,
                winnerName,
                winReason,
                enableEarlyFault,
                displayMode
            };
        });
    };

    /**
     * Detona un destello visual en pantalla al activarse la señal oficial de disparo.
     * 
     * @function triggerScreenFlash
     */
    const triggerScreenFlash = () => {
        setIsTriggerFlashActive(true);
        if (triggerFlashTimerRef.current) clearTimeout(triggerFlashTimerRef.current);
        triggerFlashTimerRef.current = setTimeout(() => {
            if (isMountedRef.current) {
                setIsTriggerFlashActive(false);
            }
            triggerFlashTimerRef.current = null;
        }, 400);
    };

    /**
     * Genera la lluvia de confeti acelerada por GPU y programa su auto-destrucción estricta tras 5.000 ms.
     * 
     * @function spawnEphemeralConfetti
     */
    const spawnEphemeralConfetti = () => {
        if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);

        const newParticles = Array.from({ length: CONFETTI_PARTICLES_COUNT }, (_, index) => {
            const randomColor = CONFETTI_COLOR_PALETTE[Math.floor(Math.random() * CONFETTI_COLOR_PALETTE.length)];
            const randomLeft = Math.floor(Math.random() * 100);
            const randomWidth = Math.floor(Math.random() * 14) + 8;
            const randomHeight = Math.floor(Math.random() * 20) + 12;
            const randomDelay = (Math.random() * 0.8).toFixed(2);
            const randomDuration = (Math.random() * 2.2 + 2.5).toFixed(2);

            return {
                id: `qr-confetti-${Date.now()}-${index}`,
                left: `${randomLeft}%`,
                width: `${randomWidth}px`,
                height: `${randomHeight}px`,
                backgroundColor: randomColor,
                animationDelay: `${randomDelay}s`,
                animationDuration: `${randomDuration}s`
            };
        });

        setEphemeralConfettiList(newParticles);

        // Auto-destrucción estricta garantizada tras 5 segundos (Zero Memory Leaks)
        confettiTimerRef.current = setTimeout(() => {
            if (isMountedRef.current) {
                setEphemeralConfettiList([]);
            }
            confettiTimerRef.current = null;
        }, 5000);
    };

    /**
     * Limpia los timers activos y destruye partículas de confeti.
     * 
     * @function clearConfettiAndTimers
     */
    const clearConfettiAndTimers = () => {
        if (confettiTimerRef.current) {
            clearTimeout(confettiTimerRef.current);
            confettiTimerRef.current = null;
        }
        if (triggerFlashTimerRef.current) {
            clearTimeout(triggerFlashTimerRef.current);
            triggerFlashTimerRef.current = null;
        }
        setEphemeralConfettiList([]);
        setIsTriggerFlashActive(false);
    };

    // Auto-limpieza estricta al desmontar el componente
    useEffect(() => {
        return () => {
            clearConfettiAndTimers();
        };
    }, []);

    // =========================================================================
    // 5. CÁLCULO DE SPRITES SEGÚN LA CINEMÁTICA Y FASE ACTUAL
    // =========================================================================
    /**
     * Determina el sprite exacto para el Jugador 1 según la fase y resultado.
     * 
     * @function getPlayerOneSprite
     * @returns {string} URL del sprite de J1.
     */
    const getPlayerOneSprite = () => {
        const { phase, winnerSide, player1 } = matchState;

        // Si la partida terminó
        if (phase === 'finished') {
            if (winnerSide === 'J1' || winnerSide === 'player1') {
                return GetQuickReactionSprite(activeThemeData, 'player1', 'victory');
            }
            if (player1.earlyFault) {
                return GetQuickReactionSprite(activeThemeData, 'player1', 'back');
            }
            return GetQuickReactionSprite(activeThemeData, 'player1', 'shoot');
        }

        // Tensión / Espera aleatoria: Personajes de espaldas
        if (phase === 'random_wait') {
            return GetQuickReactionSprite(activeThemeData, 'player1', 'back');
        }

        // Señal de disparo activa: Personajes giran al frente instantáneamente
        if (phase === 'trigger_active') {
            return GetQuickReactionSprite(activeThemeData, 'player1', 'front');
        }

        // Lobby / Countdown / Idle: Personajes de frente
        return GetQuickReactionSprite(activeThemeData, 'player1', 'front');
    };

    /**
     * Determina el sprite exacto para el Jugador 2 según la fase y resultado.
     * 
     * @function getPlayerTwoSprite
     * @returns {string} URL del sprite de J2.
     */
    const getPlayerTwoSprite = () => {
        const { phase, winnerSide, player2 } = matchState;

        // Si la partida terminó
        if (phase === 'finished') {
            if (winnerSide === 'J2' || winnerSide === 'player2') {
                return GetQuickReactionSprite(activeThemeData, 'player2', 'victory');
            }
            if (player2.earlyFault) {
                return GetQuickReactionSprite(activeThemeData, 'player2', 'back');
            }
            return GetQuickReactionSprite(activeThemeData, 'player2', 'shoot');
        }

        // Tensión / Espera aleatoria: Personajes de espaldas
        if (phase === 'random_wait') {
            return GetQuickReactionSprite(activeThemeData, 'player2', 'back');
        }

        // Señal de disparo activa: Personajes giran al frente instantáneamente
        if (phase === 'trigger_active') {
            return GetQuickReactionSprite(activeThemeData, 'player2', 'front');
        }

        // Lobby / Countdown / Idle: Personajes de frente
        return GetQuickReactionSprite(activeThemeData, 'player2', 'front');
    };

    /**
     * Determina el sprite del objetivo central único para el modo Objetivo Único (1 Personaje).
     * En este modo ambos aficionados disparan al mismo personaje en pantalla.
     * 
     * @function getSingleTargetSprite
     * @returns {string} URL del sprite central.
     */
    const getSingleTargetSprite = () => {
        const { phase, winnerSide, player1, player2 } = matchState;

        // Si la partida terminó
        if (phase === 'finished') {
            if (winnerSide === 'J1' || winnerSide === 'player1') {
                return GetQuickReactionSprite(activeThemeData, 'player1', 'victory');
            }
            if (winnerSide === 'J2' || winnerSide === 'player2') {
                return GetQuickReactionSprite(activeThemeData, 'player2', 'victory');
            }
            if (player1.earlyFault || player2.earlyFault) {
                return GetQuickReactionSprite(activeThemeData, 'player1', 'back');
            }
            return GetQuickReactionSprite(activeThemeData, 'player1', 'shoot');
        }

        // Tensión / Espera aleatoria: Personaje de espaldas
        if (phase === 'random_wait') {
            return GetQuickReactionSprite(activeThemeData, 'player1', 'back');
        }

        // Señal de disparo activa: Personaje se gira al frente / disparo instantáneamente
        if (phase === 'trigger_active') {
            return GetQuickReactionSprite(activeThemeData, 'player1', 'front');
        }

        // Lobby / Countdown / Idle: Personaje de frente
        return GetQuickReactionSprite(activeThemeData, 'player1', 'front');
    };

    // =========================================================================
    // 6. RENDERIZADO DEL HUD CENTRAL SEGÚN FASE
    // =========================================================================
    const renderCentralHud = () => {
        const { phase, countdownSeconds } = matchState;

        if (phase === 'countdown') {
            return (
                <div className="qr-screen-game__hud-countdown d-flex flex-column">
                    <img src='/img/page/xolos-five.png' style={{ width: '300px', marginBottom: '1em' }} />
                    <span className="qr-screen-game__countdown-number" key={`cd-${countdownSeconds}`}>
                        {countdownSeconds > 0 ? countdownSeconds : 1}
                    </span>
                    <span className="qr-screen-game__countdown-label">¡PREPARADOS!</span>
                </div>
            );
        }

        if (phase === 'random_wait') {
            return (
                <div className="qr-screen-game__hud-tension d-flex flex-column">
                    <img src='/img/page/xolos-five.png' style={{ width: '300px', marginBottom: '1em' }} />
                    <div className="qr-screen-game__tension-badge">
                        <span className="qr-screen-game__tension-pulse-icon">⚠️</span>
                        <span className="qr-screen-game__tension-title">¡¡DE ESPALDAS!!</span>
                        <span className="qr-screen-game__tension-subtitle">ATENTO A LA SEÑAL DE DISPARO</span>
                    </div>
                </div>
            );
        }

        if (phase === 'trigger_active') {
            return (
                <div className="qr-screen-game__hud-trigger d-flex flex-column">
                    <img src='/img/page/xolos-five.png' style={{ width: '300px', marginBottom: '1em' }} />
                    <div className="qr-screen-game__trigger-badge">
                        <span className="qr-screen-game__trigger-title">¡¡FRENTE!!</span>
                        <span className="qr-screen-game__trigger-subtitle">¡¡DISPARA YA!!</span>
                    </div>
                </div>
            );
        }

        if (phase === 'finished') {
            return (
                <div className="qr-screen-game__hud-finished d-flex flex-column">
                    <img src='/img/page/xolos-five.png' style={{ width: '300px', marginBottom: '1em' }} />
                    <span className="qr-screen-game__finished-badge">¡DUELO FINALIZADO!</span>
                </div>
            );
        }

        // Lobby por defecto (game_ready / idle)
        return (
            <div className="qr-screen-game__hud-ready d-flex flex-column">
                <img src='/img/page/xolos-five.png' style={{ width: '300px', marginBottom: '1em' }} />
                <span className="qr-screen-game__ready-badge">DUELO LISTO · EN ESPERA DEL INICIO</span>
            </div>
        );
    };

    // =========================================================================
    // 7. TEXTO DE PROCLAMACIÓN LIMPIO (ESTÉTICA ESTADIO - SIN MILISEGUNDOS)
    // =========================================================================
    const getWinReasonText = () => {
        const { winReason } = matchState;
        if (winReason === 'FASTEST_TIME') return '¡Reacción más rápida!';
        if (winReason === 'OPPONENT_FAULT') return '¡Falso inicio del rival!';
        if (winReason === 'TIMEOUT') return '¡Tiempo agotado!';
        if (winReason === 'TIE') return '¡Empate absoluto!';
        return '¡Gran Victoria en el Duelo!';
    };

    const isWinnerJ1 = matchState.phase === 'finished' && (matchState.winnerSide === 'J1' || matchState.winnerSide === 'player1');
    const isWinnerJ2 = matchState.phase === 'finished' && (matchState.winnerSide === 'J2' || matchState.winnerSide === 'player2');
    const hasWinner = isWinnerJ1 || isWinnerJ2;

    const displayMode = matchState.displayMode || activeThemeData?.displayMode || 'single_target';

    const screenBgUrl = activeThemeData?.screenBackgroundImageUrl ||
        activeThemeData?.ScreenBackgroundImageUrl;
    const resolvedBgUrl = screenBgUrl
        ? ResolveQuickReactionMediaUrl(screenBgUrl, DEFAULT_SCREEN_BG_SVG)
        : DEFAULT_SCREEN_BG_SVG;

    return (
        <main
            className="qr-screen-game"
            role="region"
            aria-label="Escenario de Combate Reacción Rápida 1 vs 1"
        >
            {/* Fondo de Arena de Combate GPU Composited */}
            <div
                className="qr-screen-game__bg-image"
                style={{ backgroundImage: `url("${resolvedBgUrl}")` }}
                aria-hidden="true"
            />

            {/* Capa de Viñeta y Resplandor de Contraste */}
            <div className="qr-screen-game__vignette" />

            {/* Destello de Disparo Full-Screen (Trigger Flash) */}
            {isTriggerFlashActive && (
                <div className="qr-screen-game__trigger-flash" aria-hidden="true" />
            )}

            {/* Lluvia de Confeti Efímero de 5 Segundos (Zero Memory Leaks) */}
            {ephemeralConfettiList.length > 0 && (
                <div className="qr-screen-game__confetti-container" aria-hidden="true">
                    {ephemeralConfettiList.map((p) => (
                        <div
                            key={p.id}
                            className="qr-screen-game__confetti-piece"
                            style={{
                                left: p.left,
                                width: p.width,
                                height: p.height,
                                backgroundColor: p.backgroundColor,
                                animationDelay: p.animationDelay,
                                animationDuration: p.animationDuration
                            }}
                        />
                    ))}
                </div>
            )}

            {/* ============================================================= */}
            {/* HUD SUPERIOR DE PANTALLA GIGANTE                             */}
            {/* ============================================================= */}
            <header className="qr-screen-game__hud-header">
                {/* Meta Duelista Lado A (Izquierda - Azul) */}
                <div className="qr-screen-game__player-card qr-screen-game__player-card--p1">
                    <div className="qr-screen-game__card-badge qr-screen-game__card-badge--p1">
                        {matchState.player1.name}
                    </div>
                    {/* <div className="qr-screen-game__card-name">
                        {matchState.player1.name}
                    </div> */}
                </div>

                {/* Centro del HUD: Dinámico según la Fase */}
                <div className="qr-screen-game__hud-center-slot">
                    {renderCentralHud()}
                </div>

                {/* Meta Duelista Lado B (Derecha - Rojo) */}
                <div className="qr-screen-game__player-card qr-screen-game__player-card--p2">
                    <div className="qr-screen-game__card-badge qr-screen-game__card-badge--p2">
                        {matchState.player2.name}
                    </div>
                    {/* <div className="qr-screen-game__card-name">
                        {matchState.player2.name}
                    </div> */}
                </div>
            </header>

            {/* ============================================================= */}
            {/* ESCENARIO DE COMBATE: MODO OBJETIVO ÚNICO O DUELO DUAL       */}
            {/* ============================================================= */}
            {displayMode === 'single_target' ? (
                <section className="qr-screen-game__arena-stage qr-screen-game__arena-stage--single-target">
                    {/* ───────────────────────────────────────────────────────── */}
                    {/* OBJETIVO CENTRAL ÚNICO (AMBOS DISPARAN AL MISMO RIVAL)   */}
                    {/* ───────────────────────────────────────────────────────── */}
                    <div className="qr-screen-game__duelist-spot qr-screen-game__duelist-spot--target">
                        {/* Halo de Victoria Brillante */}
                        {hasWinner && (
                            <div
                                className={`qr-screen-game__victory-halo ${
                                    isWinnerJ1 ? 'qr-screen-game__victory-halo--p1' : 'qr-screen-game__victory-halo--p2'
                                }`}
                                aria-hidden="true"
                            />
                        )}

                        {/* Alerta de Falso Inicio (Tarjeta Roja) si J1 o J2 cometieron falta */}
                        {(matchState.player1.earlyFault || matchState.player2.earlyFault) && (
                            <div className="qr-screen-game__early-fault-banner">
                                <img src={EARLY_FAULT_CARD_SVG} alt="Falso Inicio" className="qr-screen-game__fault-card-img" />
                                <span className="qr-screen-game__fault-banner-text">
                                    ⚠️ {matchState.player1.earlyFault ? `DISPARO ANTICIPADO: ${matchState.player1.name}` : `DISPARO ANTICIPADO: ${matchState.player2.name}`}
                                </span>
                            </div>
                        )}

                        {/* Sprite Renderizado del Objetivo Único con Fallback Inmutable */}
                        <img
                            src={getSingleTargetSprite()}
                            alt="Objetivo de Reacción Rápida"
                            className={`qr-screen-game__duelist-sprite qr-screen-game__duelist-sprite--target ${
                                hasWinner ? 'qr-screen-game__duelist-sprite--winner' : ''
                            }`}
                            loading="eager"
                            decoding="sync"
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = DEFAULT_J1_FRONT_SVG;
                            }}
                        />

                        {/* Sombra de Suelo */}
                        <div className="qr-screen-game__floor-shadow" />
                    </div>
                </section>
            ) : (
                <section className="qr-screen-game__arena-stage">
                    {/* ───────────────────────────────────────────────────────── */}
                    {/* DUELISTA LADO A (IZQUIERDA - J1 AZUL)                    */}
                    {/* ───────────────────────────────────────────────────────── */}
                    <div className={`qr-screen-game__duelist-spot qr-screen-game__duelist-spot--p1 ${isWinnerJ2 ? 'qr-screen-game__duelist-spot--defeated' : ''}`}>
                        {/* Halo de Victoria Brillante */}
                        {isWinnerJ1 && (
                            <div className="qr-screen-game__victory-halo qr-screen-game__victory-halo--p1" aria-hidden="true" />
                        )}

                        {/* Alerta de Falso Inicio (Tarjeta Roja) */}
                        {matchState.player1.earlyFault && (
                            <div className="qr-screen-game__early-fault-banner">
                                <img src={EARLY_FAULT_CARD_SVG} alt="Falso Inicio" className="qr-screen-game__fault-card-img" />
                                <span className="qr-screen-game__fault-banner-text">⚠️ DISPARO ANTICIPADO</span>
                            </div>
                        )}

                        {/* Sprite Renderizado con Fallback Inmutable */}
                        <img
                            src={getPlayerOneSprite()}
                            alt={`Duelista J1 (${matchState.player1.name})`}
                            className={`qr-screen-game__duelist-sprite qr-screen-game__duelist-sprite--p1 ${isWinnerJ1 ? 'qr-screen-game__duelist-sprite--winner' : ''}`}
                            loading="eager"
                            decoding="sync"
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = DEFAULT_J1_FRONT_SVG;
                            }}
                        />

                        {/* Sombra de Suelo */}
                        <div className="qr-screen-game__floor-shadow qr-screen-game__floor-shadow--p1" />
                    </div>

                    {/* ───────────────────────────────────────────────────────── */}
                    {/* ELEMENTO CENTRAL VS / SEÑAL                              */}
                    {/* ───────────────────────────────────────────────────────── */}
                    <div className="qr-screen-game__vs-center-slot">
                        <span className="qr-screen-game__vs-text">VS</span>
                    </div>

                    {/* ───────────────────────────────────────────────────────── */}
                    {/* DUELISTA LADO B (DERECHA - J2 ROJO)                      */}
                    {/* ───────────────────────────────────────────────────────── */}
                    <div className={`qr-screen-game__duelist-spot qr-screen-game__duelist-spot--p2 ${isWinnerJ1 ? 'qr-screen-game__duelist-spot--defeated' : ''}`}>
                        {/* Halo de Victoria Brillante */}
                        {isWinnerJ2 && (
                            <div className="qr-screen-game__victory-halo qr-screen-game__victory-halo--p2" aria-hidden="true" />
                        )}

                        {/* Alerta de Falso Inicio (Tarjeta Roja) */}
                        {matchState.player2.earlyFault && (
                            <div className="qr-screen-game__early-fault-banner">
                                <img src={EARLY_FAULT_CARD_SVG} alt="Falso Inicio" className="qr-screen-game__fault-card-img" />
                                <span className="qr-screen-game__fault-banner-text">⚠️ DISPARO ANTICIPADO</span>
                            </div>
                        )}

                        {/* Sprite Renderizado con Fallback Inmutable */}
                        <img
                            src={getPlayerTwoSprite()}
                            alt={`Duelista J2 (${matchState.player2.name})`}
                            className={`qr-screen-game__duelist-sprite qr-screen-game__duelist-sprite--p2 ${isWinnerJ2 ? 'qr-screen-game__duelist-sprite--winner' : ''}`}
                            loading="eager"
                            decoding="sync"
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = DEFAULT_J2_FRONT_SVG;
                            }}
                        />

                        {/* Sombra de Suelo */}
                        <div className="qr-screen-game__floor-shadow qr-screen-game__floor-shadow--p2" />
                    </div>
                </section>
            )}

            {/* ============================================================= */}
            {/* CARTEL DE PROCLAMACIÓN DE GANADOR LIMPIO (SIN MILISEGUNDOS)  */}
            {/* ============================================================= */}
            {hasWinner && (
                <section
                    className="qr-screen-game__podium-overlay"
                    role="alert"
                    aria-label="Proclamación de Ganador"
                >
                    <div className={`qr-screen-game__podium-card ${isWinnerJ1 ? 'qr-screen-game__podium-card--p1' : 'qr-screen-game__podium-card--p2'}`}>
                        <span className="qr-screen-game__podium-subtitle">
                            El ganador es...
                        </span>

                        <h2 className="qr-screen-game__podium-winner-name">
                            {matchState.winnerName || (isWinnerJ1 ? matchState.player1.name : matchState.player2.name) || ''}
                        </h2>

                        <div className="qr-screen-game__podium-side-badge">
                            {isWinnerJ1 ? '⚡ JUGADOR 1' : '🔥 JUGADOR 2'}
                        </div>

                        {/* Razón de Victoria Limpia para Transmisión */}
                        <p className="qr-screen-game__podium-reason">
                            {getWinReasonText()}
                        </p>
                    </div>
                </section>
            )}

            <footer className="qr-screen-cover__footer" style={{ color: '#fff' }}>
                Hecho con ♥ por Interaktia
            </footer>
        </main>
    );
};

QuickReactionGame.propTypes = {
    payload: PropTypes.shape({
        type: PropTypes.string,
        state: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object,
        theme: PropTypes.object
    }),
    currentMatchState: PropTypes.object,
    theme: PropTypes.object,
    config: PropTypes.object,
    roomId: PropTypes.string
};

QuickReactionGame.defaultProps = {
    payload: null,
    currentMatchState: null,
    theme: null,
    config: null,
    roomId: null
};

export default QuickReactionGame;
